import { computed, inject, Service, signal } from '@angular/core';
import { EPaymentMethod } from 'app/core/enums/payment-method.enum';
import { IAddress } from 'app/core/models/address/address';
import { EHiddenKey, ICountrySetting } from 'app/core/models/country/country';
import { IDeliveryRouteAddress } from 'app/core/models/delivery-route/delivery-route';
import { EAclModule } from 'app/core/permissions/enum/access.enum';
import { LaboratoryService } from 'app/core/services/laboratory/laboratory.service';
import { Observable } from 'rxjs';

declare var google: any;

@Service()
export class CountrySettingHelper {
    private readonly laboratoryService = inject(LaboratoryService);

    public readonly hiddenKeys = signal<EHiddenKey[]>([]);
    public readonly countrySetting = computed<ICountrySetting | null>(() => this.laboratoryService.countrySetting() ?? this.internalCountrySetting() ?? null);
    public readonly isGMaps = computed(() => this.countrySetting()?.settings.address.type === 'gmaps');
    public readonly internalCountrySetting = signal<ICountrySetting>(null);

    get maskPostalCode(): string {
        return this.isGMaps() ? undefined : '00000-000';
    }

    get maskPersonal(): string {
        const documents = this.countrySetting()?.settings?.properties?.document ?? [];

        if (documents.some((document) => !(document.mask?.length > 0))) {
            return undefined;
        }

        return documents
            .filter((document) => document.type === 'personal')
            .flatMap((document) => document.mask ?? '')
            .filter(Boolean)
            .join('||');
    }

    get maskBusiness(): string {
        const documents = this.countrySetting()?.settings?.properties?.document ?? [];

        if (documents.some((document) => !(document.mask?.length > 0))) {
            return undefined;
        }

        return documents
            .filter((document) => document.type === 'business')
            .flatMap((document) => document.mask ?? '')
            .filter(Boolean)
            .join('||');
    }

    get listMaskDocuments(): string[] {
        return (
            this.countrySetting()
                ?.settings?.properties?.document?.flatMap((document) => document.mask)
                .filter(Boolean) ?? []
        );
    }

    get allMaskDocuments(): string {
        const documents = this.countrySetting()?.settings?.properties?.document ?? [];

        if (documents.some((document) => !(document.mask?.length > 0))) {
            return undefined;
        }

        return documents.flatMap((document) => document.mask ?? '').join('||');
    }

    get placeholderAllDocuments(): string {
        const documents = this.countrySetting()?.settings?.properties?.document ?? [];
        return [...new Set(documents.map((document) => document.name))].join('/');
    }

    get placeholderPersonal(): string {
        const documents = this.countrySetting()?.settings?.properties?.document ?? [];
        return [...new Set(documents.filter((document) => document.type === 'personal').map((document) => document.name))].join('/');
    }

    get placeholderBusiness(): string {
        const documents = this.countrySetting()?.settings?.properties?.document ?? [];
        return [...new Set(documents.filter((document) => document.type === 'business').map((document) => document.name))].join('/');
    }

    get maskPersonalAndBusiness(): string {
        if (!this.maskPersonal || !this.maskBusiness) {
            return undefined;
        }

        return `${this.maskPersonal}||${this.maskBusiness}`;
    }

    get ipiName(): string {
        return this.countrySetting().settings.properties?.ipi?.name ?? 'IPI';
    }

    get localTaxName(): string {
        return this.countrySetting().settings.properties?.localTax?.name ?? 'ICMS';
    }

    get hiddenPaymentMethods(): EPaymentMethod[] {
        return this.countrySetting()?.hidden?.paymentMethods ?? [];
    }

    get hiddenPermissions(): EAclModule[] {
        return this.countrySetting()?.hidden?.permissions ?? [];
    }

    getPostalCodeName(postalCode: string): string {
        if (this.isGMaps()) {
            return postalCode;
        }

        return `${postalCode.substring(0, 5)}-${postalCode.substring(5)}`;
    }

    getAddressName(address: Partial<IAddress> | Partial<IDeliveryRouteAddress>): string {
        if (this.isGMaps()) {
            const arrayAddress = [address.street, address.number, address.complement, address.postalCode, address.city, address.state].filter(
                (item) => !!item,
            );

            return arrayAddress.join(', ');
        }

        return `${address.street}, ${address.number}${!!address.complement ? `, ${address.complement}` : ``}, ${
            address.district
        }, ${address.postalCode.substring(0, 5)}-${address.postalCode.substring(5)}, ${address.city}, ${address.state}`;
    }

    autocomplete(input: HTMLInputElement): Observable<Omit<IAddress, 'id'>> {
        return new Observable((observer) => {
            const autocomplete = new google.maps.places.Autocomplete(input);

            autocomplete.addListener('place_changed', () => {
                const { geometry, address_components } = autocomplete.getPlace();
                const addressComponents = address_components.reverse();

                const postalCodeSuffix = addressComponents.find((component) => component.types.includes('postal_code_suffix'))?.long_name;
                const postalCode = addressComponents.find((component) => component.types.includes('postal_code'))?.long_name;
                const streetNumber = addressComponents.find((component) => component.types.includes('street_number'))?.long_name;
                const country = addressComponents.find((component) => component.types.includes('country'))?.long_name;
                const street = addressComponents.find((component) => component.types.includes('route'))?.long_name;
                const state = addressComponents.find((component) => component.types.at(0) === 'administrative_area_level_1')?.short_name;

                const district = addressComponents.find((component) =>
                    component.types.some((type) => ['neighborhood', 'sublocality'].includes(type)),
                )?.long_name;
                const city = addressComponents.find((component) =>
                    component.types.some((type) => ['locality', 'administrative_area_level_2'].includes(type)),
                )?.long_name;
                const complement = addressComponents.find((component) => component.types.includes('subpremise'))?.long_name;

                observer.next({
                    latitude: Number(geometry.location.lat()),
                    longitude: Number(geometry.location.lng()),
                    postalCode: !!postalCode ? (postalCodeSuffix ? `${postalCode} ${postalCodeSuffix}` : postalCode) : undefined,
                    country: !!country ? country : undefined,
                    number: !!streetNumber ? streetNumber : undefined,
                    street: !!street ? street : undefined,
                    city: !!city ? city : undefined,
                    district: !!district ? district : undefined,
                    state: !!state ? state : undefined,
                    complement: !!complement ? complement : undefined,
                });
            });
        });
    }

    isHiddenByKey(key: EHiddenKey): boolean {
        return this.hiddenKeys().includes(key);
    }
}
